import React from 'react';
import { Modal, ModalButton } from './Modal';
import { modalIcons } from '../../../../config/ui/modals/modal-config';

export interface ConfirmModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Confirmation title */
  title?: string;
  /** Confirmation message */
  message: string | React.ReactNode;
  /** Confirmation variant/type */
  variant?: 'info' | 'success' | 'warning' | 'error';
  /** Modal size */
  size?: 'sm' | 'md' | 'lg';
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Whether this is a dangerous action (uses danger styling) */
  isDangerous?: boolean;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Whether to show close button in header */
  showCloseButton?: boolean;
  /** Whether clicking overlay closes modal */
  closeOnOverlayClick?: boolean;
  /** Whether escape key closes modal */
  closeOnEscape?: boolean;
  /** Custom class name */
  className?: string;
  /** Whether confirm button is disabled */
  confirmDisabled?: boolean;
  /** Whether confirm button is loading */
  confirmLoading?: boolean;
  /** Additional content to display below the message */
  children?: React.ReactNode;
}

/**
 * ConfirmModal Component
 * 
 * A specialized modal for confirmation dialogs with confirm/cancel actions.
 * Perfect for delete confirmations, action confirmations, etc.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  variant = 'warning',
  size = 'sm',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDangerous = false,
  icon,
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  confirmDisabled = false,
  confirmLoading = false,
  children,
}) => {
  // Map variants to icons if no custom icon provided
  const getVariantIcon = () => {
    if (icon) return icon;
    
    const iconMap = {
      info: modalIcons.info,
      success: modalIcons.success,
      warning: modalIcons.warning,
      error: modalIcons.error,
    };
    
    return iconMap[variant];
  };

  // Map variants to icon styles
  const getIconClassName = () => {
    const classMap = {
      info: 'app-modal-icon--info',
      success: 'app-modal-icon--success',
      warning: 'app-modal-icon--warning',
      error: 'app-modal-icon--error',
    };
    
    return classMap[variant];
  };

  // Determine confirm button variant
  const getConfirmButtonVariant = () => {
    if (isDangerous) return 'danger';
    
    const variantMap = {
      info: 'primary',
      success: 'primary',
      warning: 'primary',
      error: 'danger',
    } as const;
    
    return variantMap[variant];
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="confirm"
      size={size}
      title={title}
      icon={
        <div className={getIconClassName()}>
          {getVariantIcon()}
        </div>
      }
      className={`app-modal--confirm-${variant} ${isDangerous ? 'app-modal--dangerous' : ''} ${className}`}
      showCloseButton={showCloseButton}
      closeOnOverlayClick={closeOnOverlayClick}
      closeOnEscape={closeOnEscape}
      footer={
        <>
          <ModalButton variant="secondary" onClick={handleCancel}>
            {cancelText}
          </ModalButton>
          <ModalButton 
            variant={getConfirmButtonVariant()}
            onClick={handleConfirm}
            isDisabled={confirmDisabled}
            isLoading={confirmLoading}
          >
            {confirmText}
          </ModalButton>
        </>
      }
    >
      <div style={{ lineHeight: 1.6 }}>
        {message}
        {children && (
          <div style={{ marginTop: '16px' }}>
            {children}
          </div>
        )}
      </div>
    </Modal>
  );
};

/**
 * Convenience components for specific confirmation types
 */
export const DeleteConfirmModal: React.FC<Omit<ConfirmModalProps, 'variant' | 'isDangerous' | 'confirmText'> & { 
  itemName?: string;
  confirmText?: string;
}> = ({ itemName, confirmText = 'Delete', ...props }) => (
  <ConfirmModal 
    {...props} 
    variant="error" 
    isDangerous={true}
    confirmText={confirmText}
    title={props.title || `Delete ${itemName || 'Item'}`}
  />
);

export const SaveConfirmModal: React.FC<Omit<ConfirmModalProps, 'variant' | 'confirmText'> & { 
  confirmText?: string;
}> = ({ confirmText = 'Save Changes', ...props }) => (
  <ConfirmModal 
    {...props} 
    variant="info"
    confirmText={confirmText}
    title={props.title || 'Save Changes'}
  />
);

export const DiscardConfirmModal: React.FC<Omit<ConfirmModalProps, 'variant' | 'isDangerous' | 'confirmText'> & { 
  confirmText?: string;
}> = ({ confirmText = 'Discard', ...props }) => (
  <ConfirmModal 
    {...props} 
    variant="warning" 
    isDangerous={true}
    confirmText={confirmText}
    title={props.title || 'Discard Changes'}
  />
);

export const LogoutConfirmModal: React.FC<Omit<ConfirmModalProps, 'variant' | 'confirmText'> & { 
  confirmText?: string;
}> = ({ confirmText = 'Logout', ...props }) => (
  <ConfirmModal 
    {...props} 
    variant="warning"
    confirmText={confirmText}
    title={props.title || 'Confirm Logout'}
  />
);

export default ConfirmModal;